import { useState } from 'react';
import { DateRangeField } from './filter-date/date-range-field';
import { DATE_SEPARATOR, formatDateRange, parseDateValue } from './filter-date/filter-date-utils';
import { SingleDateField } from './filter-date/single-date-field';

interface FilterDateFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hasValue?: boolean;
  allowToggle?: boolean;
  defaultToRangeMode?: boolean;
}

const FilterDateField = ({
  label,
  value,
  onChange,
  hasValue = false,
  allowToggle = false,
  defaultToRangeMode = true,
}: FilterDateFieldProps) => {
  const [isRangeMode, setIsRangeMode] = useState(defaultToRangeMode);

  const parsedValue = parseDateValue(value);
  const { isRange: isCurrentValueRange, fromDate, toDate } = parsedValue;

  const handleModeToggle = () => {
    setIsRangeMode(prev => !prev);
    onChange('');
  };

  const handleFromDateChange = (newValue: string) => {
    if (!toDate) {
      // Allow selecting from date first - format with colon separator for API
      onChange(formatDateRange(newValue, ''));
      return;
    }

    // If the new from date is after the to date, clear the to date
    // This allows users to select dates in any order without confusion
    if (newValue && toDate && newValue > toDate) {
      onChange(formatDateRange(newValue, ''));
      return;
    }

    // Always keep both dates - let validation error guide the user
    onChange(formatDateRange(newValue, toDate));
  };

  const handleToDateChange = (newValue: string) => {
    if (!fromDate) {
      // Allow selecting to date first - format with colon separator for API
      onChange(formatDateRange('', newValue));
      return;
    }

    if (!newValue) {
      onChange(formatDateRange(fromDate, ''));
      return;
    }

    // Always update the to date - let validation error guide the user
    onChange(formatDateRange(fromDate, newValue));
  };

  const shouldShowRangeMode = isRangeMode || isCurrentValueRange;

  // When switching to single date mode, extract just the date value without the separator
  const handleSingleDateChange = (newValue: string) => {
    // In single date mode, we should only send the date value without the separator
    // If the value contains a separator, extract just the date part
    if (newValue.includes(DATE_SEPARATOR)) {
      const parsed = parseDateValue(newValue);
      // Prefer fromDate if it exists, otherwise use toDate
      onChange(parsed.fromDate || parsed.toDate || '');
    } else {
      onChange(newValue);
    }
  };

  // When in single date mode, ensure the value doesn't contain the separator
  const singleDateValue = shouldShowRangeMode
    ? value
    : value.includes(DATE_SEPARATOR)
      ? parseDateValue(value).fromDate || parseDateValue(value).toDate || ''
      : value;

  if (shouldShowRangeMode) {
    return (
      <DateRangeField
        label={label}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
        hasValue={hasValue}
        allowToggle={allowToggle}
        onToggle={handleModeToggle}
      />
    );
  }

  return (
    <SingleDateField
      label={label}
      value={singleDateValue}
      onChange={handleSingleDateChange}
      hasValue={hasValue}
      allowToggle={allowToggle}
      onToggle={handleModeToggle}
    />
  );
};

export default FilterDateField;
